<template>
    <div>
        <div v-if="loading" class="text-center text-muted py-4">
            <i class="fa fa-spinner fa-spin me-1" aria-hidden="true"></i> Loading...
        </div>
        <div v-if="error" class="alert alert-danger mt-3">{{ error }}</div>

        <template v-if="!loading && !error">
            <div v-if="untaggedCount > 0" class="alert alert-warning py-2 mt-2 small">
                <i class="fa fa-exclamation-triangle me-1" aria-hidden="true"></i>
                {{ untaggedCount }} transaction{{ untaggedCount === 1 ? '' : 's' }} without an owner.
            </div>

            <p v-if="!transactions.length" class="text-muted mb-0 mt-3">No transactions for {{ year }}.</p>

            <div v-for="dirGroup in directionGroups" :key="dirGroup.direction">
                <h6 :class="['fw-semibold mt-3 mb-2', 'text-' + dirGroup.color]">
                    <i :class="'fa fa-' + dirGroup.icon + ' me-1'" aria-hidden="true"></i>
                    {{ dirGroup.label }}
                </h6>

                <div v-for="typeGroup in dirGroup.types" :key="typeGroup.type">
                    <p v-if="typeGroup.label" class="text-muted small mb-2 mt-3">
                        <i :class="'fa fa-' + typeGroup.icon + ' me-1'" aria-hidden="true"></i>
                        {{ typeGroup.label }}
                    </p>

                    <div v-for="currGroup in typeGroup.currencies" :key="currGroup.key"
                        class="card mb-3 rounded-3 shadow-sm border-0">
                        <div class="card-header bg-light border-0 py-3">
                            <button class="btn btn-link w-100 text-start text-decoration-none p-0 text-body d-flex align-items-center gap-2"
                                type="button" @click="toggleGroup(currGroup.key)">
                                <span class="flex-grow-1 d-flex align-items-center gap-2">
                                    <span class="badge bg-secondary">{{ currGroup.currency }}</span>
                                    <template v-if="!isGroupOpen(currGroup.key)">
                                        <span class="fw-semibold">{{ fmt(currGroup.totalNative, 2) }} {{ currGroup.currency }}</span>
                                        <span v-if="currGroup.currency !== 'EUR'" class="text-muted fw-normal">
                                            ≈ {{ fmt(currGroup.totalEur, 2) }} EUR
                                        </span>
                                    </template>
                                    <small class="text-muted fw-normal">{{ currGroup.txs.length }} transactions</small>
                                </span>
                                <i class="fa fa-chevron-down flex-shrink-0 text-secondary" aria-hidden="true"></i>
                            </button>
                        </div>

                        <div v-show="isGroupOpen(currGroup.key)">
                            <div class="card-body p-0">
                                <table class="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th class="ps-3 text-nowrap tx-shrink-col">Date</th>
                                            <th>Counterparty / Description</th>
                                            <th class="pe-3 pe-md-1 text-end text-nowrap tx-shrink-col">Amount</th>
                                            <th class="pe-3 text-end text-nowrap d-none d-md-table-cell tx-shrink-col">Category / Owner</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <template v-for="tx in currGroup.txs">
                                            <!-- Primary row -->
                                            <tr :key="'p-' + tx.id" class="border-bottom-0"
                                                :class="{ 'tx-hovered': hoveredTxId === tx.id }"
                                                @mouseenter="hoveredTxId = tx.id" @mouseleave="hoveredTxId = null">
                                                <td class="ps-3 text-nowrap text-muted align-middle tx-no-border">
                                                    {{ tx.booking_date }}
                                                </td>
                                                <td class="align-middle tx-no-border tx-truncate-cell">
                                                    <div class="text-truncate" :title="tx.counterparty_name || ''">
                                                        {{ tx.counterparty_name || '—' }}
                                                    </div>
                                                </td>
                                                <td class="pe-3 pe-md-1 text-end text-nowrap align-middle tx-no-border">
                                                    {{ fmt(tx.amount, 2) }} {{ currGroup.currency }}
                                                </td>
                                                <td class="pe-3 text-end text-nowrap align-middle d-none d-md-table-cell tx-no-border">
                                                    <div class="dropdown d-inline-block">
                                                        <button type="button"
                                                            class="btn btn-link btn-sm p-0 text-decoration-none"
                                                            data-bs-toggle="dropdown"
                                                            data-bs-auto-close="true">
                                                            <span v-if="tx.category_id" class="badge"
                                                                :style="tx.category_color ? { background: tx.category_color } : {}"
                                                                :class="tx.category_color ? '' : 'bg-secondary'">
                                                                {{ tx.category_name }}
                                                            </span>
                                                            <span v-else class="badge bg-light text-muted border">—</span>
                                                        </button>
                                                        <ul class="dropdown-menu dropdown-menu-end">
                                                            <li v-for="cat in categories" :key="cat.id">
                                                                <button type="button" class="dropdown-item d-flex align-items-center gap-2"
                                                                    :class="{ active: tx.category_id === cat.id }"
                                                                    @click="assignCategory(tx, cat.id)">
                                                                    <span v-if="cat.color"
                                                                        class="tx-color-dot"
                                                                        :style="{ background: cat.color }"></span>
                                                                    {{ cat.name }}
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                            <!-- Secondary row -->
                                            <tr :key="'s-' + tx.id" class="text-muted small"
                                                :class="{ 'tx-hovered': hoveredTxId === tx.id }"
                                                @mouseenter="hoveredTxId = tx.id" @mouseleave="hoveredTxId = null">
                                                <td class="ps-3 tx-sub-cell tx-no-border"></td>
                                                <td class="tx-sub-cell tx-no-border tx-truncate-cell"
                                                    :title="tx.remittance_info || ''">
                                                    <div class="text-truncate">{{ tx.remittance_info || '—' }}</div>
                                                </td>
                                                <td class="pe-3 pe-md-1 text-end text-nowrap tx-sub-cell tx-no-border">
                                                    <span v-if="currGroup.currency !== 'EUR' && tx.rate">
                                                        @ {{ fmt(tx.rate, 4) }} → {{ fmt(tx.eur, 2) }} EUR
                                                    </span>
                                                </td>
                                                <td class="pe-3 text-end text-nowrap d-none d-md-table-cell tx-sub-cell tx-no-border">
                                                    <div class="dropdown d-inline-block">
                                                        <button type="button"
                                                            class="btn btn-link btn-sm p-0 text-decoration-none"
                                                            data-bs-toggle="dropdown"
                                                            data-bs-auto-close="true">
                                                            <span v-if="tx.owner_user_id" class="badge"
                                                                :class="ownerBadgeClass(tx.owner_user_id)"
                                                                :title="tx.owner_source === 'manual' ? 'Manual' : 'Auto'">
                                                                {{ tx.owner_name }}
                                                            </span>
                                                            <span v-else class="badge bg-light text-muted border">—</span>
                                                        </button>
                                                        <ul class="dropdown-menu dropdown-menu-end">
                                                            <li v-for="user in users" :key="user.id">
                                                                <button type="button" class="dropdown-item"
                                                                    :class="{ active: tx.owner_user_id === user.id }"
                                                                    @click="assignOwner(tx, user.id)">
                                                                    {{ user.name }}
                                                                </button>
                                                            </li>
                                                            <li><hr class="dropdown-divider"></li>
                                                            <li>
                                                                <button type="button" class="dropdown-item text-muted"
                                                                    @click="assignOwner(tx, null)">
                                                                    Unassign
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                            <!-- Mobile-only third row: category + owner -->
                                            <tr :key="'m-' + tx.id" class="d-md-none text-muted small"
                                                :class="{ 'tx-hovered': hoveredTxId === tx.id }"
                                                @mouseenter="hoveredTxId = tx.id" @mouseleave="hoveredTxId = null">
                                                <td class="ps-3 tx-sub-cell"></td>
                                                <td class="tx-sub-cell tx-truncate-cell">
                                                    <div class="dropdown d-inline-block">
                                                        <button type="button"
                                                            class="btn btn-link btn-sm p-0 text-decoration-none"
                                                            data-bs-toggle="dropdown"
                                                            data-bs-auto-close="true">
                                                            <span v-if="tx.category_id" class="badge tx-badge-truncate"
                                                                :style="tx.category_color ? { background: tx.category_color } : {}"
                                                                :class="tx.category_color ? '' : 'bg-secondary'">
                                                                {{ tx.category_name }}
                                                            </span>
                                                            <span v-else class="badge bg-light text-muted border">—</span>
                                                        </button>
                                                        <ul class="dropdown-menu">
                                                            <li v-for="cat in categories" :key="cat.id">
                                                                <button type="button" class="dropdown-item d-flex align-items-center gap-2"
                                                                    :class="{ active: tx.category_id === cat.id }"
                                                                    @click="assignCategory(tx, cat.id)">
                                                                    <span v-if="cat.color"
                                                                        class="tx-color-dot"
                                                                        :style="{ background: cat.color }"></span>
                                                                    {{ cat.name }}
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                                <td class="text-end pe-3 tx-sub-cell">
                                                    <div class="dropdown d-inline-block">
                                                            <button type="button"
                                                                class="btn btn-link btn-sm p-0 text-decoration-none"
                                                                data-bs-toggle="dropdown"
                                                                data-bs-auto-close="true">
                                                                <span v-if="tx.owner_user_id" class="badge"
                                                                    :class="ownerBadgeClass(tx.owner_user_id)"
                                                                    :title="tx.owner_source === 'manual' ? 'Manual' : 'Auto'">
                                                                    {{ tx.owner_name }}
                                                                </span>
                                                                <span v-else class="badge bg-light text-muted border">—</span>
                                                            </button>
                                                            <ul class="dropdown-menu dropdown-menu-end">
                                                            <li v-for="user in users" :key="user.id">
                                                                <button type="button" class="dropdown-item"
                                                                    :class="{ active: tx.owner_user_id === user.id }"
                                                                    @click="assignOwner(tx, user.id)">
                                                                    {{ user.name }}
                                                                </button>
                                                            </li>
                                                            <li><hr class="dropdown-divider"></li>
                                                            <li>
                                                                <button type="button" class="dropdown-item text-muted"
                                                                    @click="assignOwner(tx, null)">
                                                                    Unassign
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                    </tbody>
                                    <tfoot>
                                        <tr :class="['fw-semibold', 'table-' + dirGroup.color]">
                                            <td colspan="4" class="pe-3 text-end text-nowrap">
                                                Total
                                                {{ fmt(currGroup.totalNative, 2) }} {{ currGroup.currency }}
                                                <span v-if="currGroup.currency !== 'EUR'" class="fw-normal text-muted ms-1">
                                                    ≈ {{ fmt(currGroup.totalEur, 2) }} EUR
                                                </span>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
const DIRECTION_META = {
    credit: { label: 'Credits', color: 'success', icon: 'arrow-down' },
    debit:  { label: 'Debits',  color: 'danger',  icon: 'arrow-up'   },
};

const TYPE_ORDER = ['regular', 'salaries', 'taxes', 'fx', 'fees'];

const TYPE_META = {
    regular:  { label: null,             icon: 'tag'      },
    salaries: { label: 'Salaries',       icon: 'users'    },
    taxes:    { label: 'Taxes',          icon: 'building' },
    fx:       { label: 'FX Conversions', icon: 'exchange' },
    fees:     { label: 'Bank Fees',      icon: 'bank'     },
};

const OWNER_COLORS = ['bg-info', 'bg-warning', 'bg-success', 'bg-danger', 'bg-purple'];

export default {
    name: 'TransactionList',

    props: {
        year:         { type: Number, required: true },
        dataUrl:      { type: String, required: true },
        patchBaseUrl: { type: String, required: true },
    },

    data() {
        return {
            loading:      true,
            error:        null,
            transactions: [],
            users:        [],
            categories:   [],
            openGroups:   {},
            hoveredTxId:  null,
        };
    },

    computed: {
        directionGroups() {
            const byDir = {};
            for (const tx of this.transactions) {
                if (!byDir[tx.direction])                        byDir[tx.direction] = {};
                if (!byDir[tx.direction][tx.type])               byDir[tx.direction][tx.type] = {};
                if (!byDir[tx.direction][tx.type][tx.currency])  byDir[tx.direction][tx.type][tx.currency] = [];
                byDir[tx.direction][tx.type][tx.currency].push(tx);
            }

            return ['credit', 'debit']
                .filter(dir => byDir[dir])
                .map(dir => ({
                    direction: dir,
                    ...DIRECTION_META[dir],
                    types: TYPE_ORDER
                        .filter(type => byDir[dir][type])
                        .map(type => ({
                            type,
                            ...TYPE_META[type],
                            currencies: Object.entries(byDir[dir][type]).map(([currency, txs]) => {
                                const key = `${dir}-${type}-${currency}`;
                                return {
                                    key,
                                    currency,
                                    txs,
                                    totalNative: txs.reduce((s, t) => s + t.amount, 0),
                                    totalEur:    txs.reduce((s, t) => s + (t.eur ?? 0), 0),
                                };
                            }),
                        })),
                }));
        },

        untaggedCount() {
            return this.transactions.filter(t => !t.owner_user_id).length;
        },
    },

    mounted() {
        this.fetchData();
    },

    methods: {
        async fetchData() {
            this.loading = true;
            this.error   = null;
            try {
                const res          = await axios.get(this.dataUrl);
                this.transactions  = res.data.transactions;
                this.users         = res.data.users;
                this.categories    = res.data.categories;
            } catch (e) {
                this.error = 'Failed to load transactions.';
            } finally {
                this.loading = false;
            }
        },

        isGroupOpen(key) {
            return this.openGroups[key] !== false;
        },

        toggleGroup(key) {
            this.$set(this.openGroups, key, !this.isGroupOpen(key));
        },

        ownerBadgeClass(userId) {
            const idx = this.users.findIndex(u => u.id === userId);
            return OWNER_COLORS[idx % OWNER_COLORS.length] || 'bg-secondary';
        },

        fmt(value, decimals) {
            if (value === null || value === undefined) return '—';
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(value);
        },

        async assignOwner(tx, userId) {
            const url = `${this.patchBaseUrl}/${tx.id}/owner`;
            try {
                const res = await axios.patch(url, { user_id: userId });
                const idx = this.transactions.findIndex(t => t.id === tx.id);
                if (idx !== -1) {
                    Object.assign(this.transactions[idx], {
                        owner_user_id: res.data.owner_user_id,
                        owner_name:    res.data.owner_name,
                        owner_source:  res.data.owner_source,
                    });
                }
            } catch (e) {
                console.error('Failed to update owner', e);
            }
        },

        async assignCategory(tx, categoryId) {
            const url = `${this.patchBaseUrl}/${tx.id}/category`;
            try {
                const res = await axios.patch(url, { category_id: categoryId });
                const idx = this.transactions.findIndex(t => t.id === tx.id);
                if (idx !== -1) {
                    Object.assign(this.transactions[idx], {
                        category_id:    res.data.category_id,
                        category_name:  res.data.category_name,
                        category_color: res.data.category_color,
                    });
                }
            } catch (e) {
                console.error('Failed to update category', e);
            }
        },
    },
};
</script>
